import React, { useState, useMemo, useEffect } from "react";
import { colors, fonts, frames, glyphs } from "../../../../../Styles";
import { PageHeader, FilterPopup, WaitAnimation, useAicoConnection, AicoConnection } from "../../../../../Components";
import { ListData_ValueTypes, TableStyles_Types, ButtonTypes } from "../../../../../Definitions";

// Reactive Table
import { ColumnFiltersState, useReactTable, getCoreRowModel, getSortedRowModel, getFilteredRowModel, SortingState, getPaginationRowModel } from "@tanstack/react-table";
import globalFilter from "../../../../../Components/Tables/_Tables/useGlobalFilter";
import { ReactiveTable } from "../../../../../Components/Tables/reactiveTableRenderer";

// Data
import { useQuery } from "@apollo/client";
import { CONTROLLER_LOG_AICO, LanControllerLogAicoArgs, LanControllerLogAicoResponse } from "../../../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/controllerlogaico";
import { useTranslation } from "react-i18next";
import { useColumnConstructor } from "../../../../../Components/Tables/_Tables/useColumnConstructor";
import NewButton, { NewButtonList } from "../../../../../Components/BaseComponents/newButton";

const levelMap: Record<number, string> = {
  0: "Error",
  1: "Warning",
  2: "Info",
  3: "Debug",
};
const typeMap: Record<number, string> = {
  1: "System",
  2: "Motor",
  3: "Animal",
};

const getLevelOptions = Object.entries(levelMap).map(([key, value]) => ({label: value, id: +key}));

const getTypeOptions = Object.entries(typeMap).map(([key, value]) => ({ label: value, id: +key }));

const accessorData = [
  { headerValue: "Time", accessor: "timestamp", type: ListData_ValueTypes.Date, sort: true, width: "200px" },
  { headerValue: "Type", accessor: "type", type: ListData_ValueTypes.List, sort: true, width: "120px", filter: true, options: getTypeOptions },
  { headerValue: "Level", accessor: "level", type: ListData_ValueTypes.List, sort: true, width: "120px", filter: true, options: getLevelOptions },
  { headerValue: "Message", accessor: "message", type: ListData_ValueTypes.String, sort: true },
  { headerValue: "Lancontroller", accessor: "lan_controller", type: ListData_ValueTypes.String, sort: true, width: "200px" },
  { headerValue: "IO", accessor: "device", type: ListData_ValueTypes.String, sort: true, width: "120px" },
  { headerValue: "Terminal", accessor: "terminals", type: ListData_ValueTypes.String, sort: true, width: "120px" },
  { headerValue: "Animals", accessor: "animals", type: ListData_ValueTypes.String, sort: true, width: "120px" },
]

export default function LogViewBlock() {
  const {t} = useTranslation()

  const { state: connectionState } = useAicoConnection();


  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const { data, loading, error, refetch } = useQuery<LanControllerLogAicoResponse>( CONTROLLER_LOG_AICO, {
    variables: connectionState.deviceConnection
      ? { domain: connectionState.deviceConnection.domain,
          input: { ids: [connectionState.deviceConnection.lanControllerId], offset: 0, fetchAmount: 501 },
        } : undefined,
    skip: !connectionState,
    fetchPolicy: "network-only", 
    notifyOnNetworkStatusChange: true,
  });


  const transformMessage = (msg: string): string => {
    if (typeof msg !== "string") return String(msg ?? "");
    return msg
      .replace(/\bF(\d+)/g, "feedcurve $1")
      .replace(/\bM(\d+)/g, "Milestone $1")
      .replace(/\bD(\d+)/g, "Day $1");
  };

 const tableData = useMemo(() => {
  if (!data?.controller?.controllerLogsAico) return [];
  return data.controller.controllerLogsAico.map((log: LanControllerLogAicoArgs) => ({
    id: log.lan_controller_log_id,
    timestamp: new Date(log.timestamp * 1000),
    level: { label: levelMap[log.level], id: log.level },
    type: { label: typeMap[log.type], id: log.type },
    message: transformMessage(log.message),
    terminals: log.port ? `Port: ${log.port}` : "",
    animals: log.fk_animals ?? "",
    lan_controller: log.lan_controller_cpu_id ? `CPU: ${log.lan_controller_cpu_id}` : "",
    device: log.device_id ? `Device: ${log.device_id}` : "",
  }));
}, [data]);

  const columns = useColumnConstructor({
    accessorData,
    tableStyle: TableStyles_Types.primaryTable,
    canEdit: false,
  });

  const pageSize = 500;

  const totalLogs = data?.controller?.controllerLogsAico.length ?? 0;

  const totalPages = Math.ceil(totalLogs / pageSize);

  const table = useReactTable({
    columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    manualPagination: true,
    globalFilterFn: globalFilter,
    pageCount: totalPages,
    state: {
      globalFilter: globalFilterValue,
      columnFilters,
    },
    initialState: {
      sorting: [{ id: "timestamp", desc: true }],
      pagination: { pageSize },
    },
  });

  const pageCount = useMemo(() => (
    loading ? <WaitAnimation color={colors.greyLightTertiary}/> :
    <p style={{ ...fonts.fontSize4, ...fonts.header, color: colors.greyLightTertiary }}>
      {`${t("page", { defaultValue: "page" })} ${totalLogs === 0 ? 0 : table.getState().pagination.pageIndex + 1} ${t("of", { defaultValue: "of" })} ${totalPages}`}
    </p>
  ), [loading, t, table, totalLogs, totalPages]);

  return (
    <frames.Page_inner>
        <PageHeader topHeader='Aico Log' subHeader='Log'/>

      <frames._page_inner_content>
        <frames._grid_topBtns_outer style={{gridAutoRows: "50px 1fr 40px"}}>

          <frames._grid_topBtns_inner style={{position: "relative", justifyContent: "end"}}>  
            <div style={{ position: "absolute", top: "50%", transform: "translateY(-50%)", left: "20px" }}>
              <AicoConnection/>
            </div>
            <div style={{ display: "flex", gap: "5px" }}>
              <FilterPopup accessorData={accessorData} table={table} />
              <NewButtonList
                contents={["fetch"]}
                disabled={[false]}
                buttonType={ButtonTypes.tableButton}
                startEnhancers={[ glyphs.Fetch({}) ]}
                onClick={(i => { 
                  if (i === 0) {
                    table.firstPage();
                    refetch({
                      input: {
                        ids: [connectionState.lanControllerId ?? 0],
                        fetchAmount: 501,
                        offset: 0,
                      },
                    });
                  } 
                })}
              />
          </div>
          </frames._grid_topBtns_inner>
          
          <div style={{gridArea: "2/1/3/2", border: "5px solid "+colors.greyPrimary, overflow: "hidden", borderRadius: "10px", backgroundColor: colors.greyLightTertiary }}>
            <ReactiveTable
              table={table}
              tableType={TableStyles_Types.primaryTable}
              emptyText="No logs available"
              renderFooter={false}
              loading={loading}
            />
          </div>
          <div style={{ gridArea: "3/1/4/2", display: "flex", alignItems: "center", gap: "5px", padding: "5px 20px 0px 20px" }}>
            <NewButton
              content={""}
              onClick={() => {
                if (table.getCanPreviousPage()) {
                  refetch({
                    input: {
                      ids: [connectionState.lanControllerId ?? 0],
                      fetchAmount: 501,
                      offset: 500 * (table.getState().pagination.pageIndex - 1),
                    },
                  });
                  table.previousPage();
                }
              }}
              buttonType={ButtonTypes.arrowButton}
              startEnhancer={glyphs.Arrow({ rotate: 180 })}
              overrides={{
                baseStyles: {backgroundColor: colors.greyPrimary},
                hoverStyles: {backgroundColor: colors.greySecondary},
              }}
              disabled={!(table.getCanPreviousPage())}
            />
            <NewButton
              content={""}
              buttonType={ButtonTypes.arrowButton}
              onClick={() => {
                if (table.getCanNextPage() && (data?.controller.controllerLogsAico.length && data.controller.controllerLogsAico.length > 500)) {
                  refetch({
                    input: {
                      ids: [connectionState.lanControllerId ?? 0],
                      fetchAmount: 501,
                      offset: 500 * (table.getState().pagination.pageIndex + 1),
                    },
                  });
                  table.nextPage();
                }
              }}
              startEnhancer={glyphs.Arrow({})}
              overrides={{
                baseStyles: {backgroundColor: colors.greyPrimary},
                hoverStyles: {backgroundColor: colors.greySecondary},
              }}
              disabled={!(table.getCanNextPage())}
            />
            <>{pageCount}</>
          </div>
        </frames._grid_topBtns_outer>
      </frames._page_inner_content>
    </frames.Page_inner>
  );
}